import { useState, useEffect, useCallback } from 'react'
import {
  getAdminEvents,
  updateAdminEvent,
  deleteAdminEvent,
  createAdminEvent,
  bulkUpdateEventStatus,
  triggerEventIngestion,
  type EventSummary,
  type EventFilters,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { EventCreateModal } from './EventCreateModal'

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'past', label: 'Past' },
]

const sourceOptions = [
  { value: '', label: 'All Sources' },
  { value: 'ticketmaster', label: 'Ticketmaster' },
  { value: 'eventbrite', label: 'Eventbrite' },
  { value: 'admin', label: 'Admin' },
]

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    disabled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    past: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[status] || colors.past}`}>
      {status}
    </span>
  )
}

function SourceBadge({ source }: { source: string }) {
  const colors: Record<string, string> = {
    ticketmaster: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    eventbrite: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[source] || 'bg-gray-100 text-gray-600'}`}>
      {source}
    </span>
  )
}

export function Events() {
  const [events, setEvents] = useState<EventSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filters, setFilters] = useState<EventFilters>({
    status: '',
    source: '',
    search: '',
    page: 1,
    page_size: 20,
  })

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  useEffect(() => {
    setFilters((prev) => (prev.search === debouncedSearch ? prev : { ...prev, search: debouncedSearch, page: 1 }))
  }, [debouncedSearch])

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkStatus, setBulkStatus] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [ingesting, setIngesting] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  // Inline editing state
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null)
  const [editValue, setEditValue] = useState('')

  const canEdit = usePermission('discover_events', 'edit')
  const canCreate = usePermission('discover_events', 'create')
  const canDelete = usePermission('discover_events', 'delete')
  const canTrigger = usePermission('discover_events', 'trigger_ingestion')

  const loadEvents = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getAdminEvents(filters)
      if (response.ok && response.data) {
        setEvents(response.data.items)
        setTotal(response.data.total)
        setPage(response.data.page)
        setTotalPages(response.data.total_pages)
      } else {
        setError(response.error?.message || 'Failed to load events')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  const handleFilterChange = (key: keyof EventFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }))
  }

  const handleSelectEvent = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedIds.size === events.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(events.map((e) => e.id)))
    }
  }

  // Inline edit handlers
  const startEditing = (id: string, field: string, currentValue: string) => {
    if (!canEdit) return
    setEditingCell({ id, field })
    setEditValue(currentValue)
  }

  const saveEdit = async () => {
    if (!editingCell) return
    const { id, field } = editingCell
    const update: Record<string, string> = { [field]: editValue }
    const response = await updateAdminEvent(id, update)
    if (response.ok) {
      loadEvents()
    }
    setEditingCell(null)
  }

  const cancelEdit = () => {
    setEditingCell(null)
  }

  const handleStatusChange = async (id: string, newStatus: string) => {
    const response = await updateAdminEvent(id, { status: newStatus })
    if (response.ok) {
      loadEvents()
    }
  }

  const handleDelete = async (id: string) => {
    const response = await deleteAdminEvent(id)
    if (response.ok) {
      setDeleteConfirmId(null)
      loadEvents()
    }
  }

  const handleBulkAction = async () => {
    if (!bulkStatus || selectedIds.size === 0) return
    const response = await bulkUpdateEventStatus(Array.from(selectedIds), bulkStatus)
    if (response.ok) {
      setSelectedIds(new Set())
      setBulkStatus('')
      loadEvents()
    }
  }

  const handleTriggerIngestion = async () => {
    setIngesting(true)
    try {
      const response = await triggerEventIngestion()
      if (response.ok) {
        setError(null)
      } else {
        setError(response.error?.message || 'Failed to trigger ingestion')
      }
    } catch {
      setError('Failed to trigger ingestion')
    } finally {
      setIngesting(false)
    }
  }

  const handleCreate = async (data: {
    name: string
    description?: string
    starts_at: string
    city?: string
    state?: string
    categories?: string[]
    url?: string
    image_url?: string
  }) => {
    const response = await createAdminEvent(data)
    if (response.ok) {
      loadEvents()
    } else {
      throw new Error(response.error?.message || 'Failed to create event')
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-white">Events</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{total} total events</p>
        </div>
        <div className="flex gap-2">
          {canTrigger && (
            <CustomButton variant="secondary" onClick={handleTriggerIngestion} loading={ingesting}>
              Trigger Ingestion
            </CustomButton>
          )}
          {canCreate && (
            <CustomButton variant="primary" onClick={() => setShowCreateModal(true)}>
              Add Event
            </CustomButton>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="w-64">
          <CustomTextInput
            label=""
            placeholder="Search events..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          value={filters.status || ''}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm text-gray-900 dark:text-white focus:border-[#503864] focus:outline-none focus:ring-1 focus:ring-[#503864]"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={filters.source || ''}
          onChange={(e) => handleFilterChange('source', e.target.value)}
          className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm text-gray-900 dark:text-white focus:border-[#503864] focus:outline-none focus:ring-1 focus:ring-[#503864]"
        >
          {sourceOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Bulk Actions */}
      {canEdit && selectedIds.size > 0 && (
        <div className="mb-6 rounded-xl bg-gray-50 dark:bg-[#2d2236] p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {selectedIds.size} selected
            </span>
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              className="rounded-lg border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-3 py-1.5 text-sm text-gray-900 dark:text-white"
            >
              <option value="">Set status...</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
              <option value="past">Past</option>
            </select>
            {bulkStatus && (
              <CustomButton onClick={handleBulkAction} variant="primary">Apply</CustomButton>
            )}
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-6 rounded-xl bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#503864] border-t-transparent" />
        </div>
      ) : events.length === 0 ? (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">No events found</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-[#3d2c4f]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-[#3d2c4f] bg-gray-50 dark:bg-[#1a1520]">
                {canEdit && (
                  <th className="px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === events.length && events.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-[#503864] focus:ring-[#503864]"
                    />
                  </th>
                )}
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Name</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Source</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Categories</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Date</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Location</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Status</th>
                {(canEdit || canDelete) && (
                  <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} className="border-b border-gray-100 dark:border-[#3d2c4f] hover:bg-gray-50 dark:hover:bg-[#2d2236]">
                  {canEdit && (
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(event.id)}
                        onChange={() => handleSelectEvent(event.id)}
                        className="h-4 w-4 rounded border-gray-300 text-[#503864] focus:ring-[#503864]"
                      />
                    </td>
                  )}
                  {/* Name - editable */}
                  <td className="px-3 py-3 max-w-xs">
                    {editingCell?.id === event.id && editingCell?.field === 'name' ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={saveEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit()
                          if (e.key === 'Escape') cancelEdit()
                        }}
                        autoFocus
                        className="w-full rounded border border-[#503864] bg-white dark:bg-[#1a1520] px-2 py-1 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#503864]"
                      />
                    ) : (
                      <span
                        className={`truncate block text-gray-900 dark:text-white ${canEdit ? 'cursor-pointer hover:text-[#503864] dark:hover:text-[#b58fcc]' : ''}`}
                        onDoubleClick={() => startEditing(event.id, 'name', event.name)}
                        title={event.name}
                      >
                        {event.name}
                      </span>
                    )}
                  </td>
                  {/* Source */}
                  <td className="px-3 py-3">
                    <SourceBadge source={event.source} />
                  </td>
                  {/* Categories */}
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {event.categories.slice(0, 3).map((cat) => (
                        <span key={cat} className="inline-flex rounded-full bg-gray-100 dark:bg-[#2d2236] px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300">
                          {cat}
                        </span>
                      ))}
                      {event.categories.length > 3 && (
                        <span className="text-xs text-gray-500">+{event.categories.length - 3}</span>
                      )}
                    </div>
                  </td>
                  {/* Date */}
                  <td className="px-3 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                    {formatDate(event.starts_at)}
                  </td>
                  {/* Location */}
                  <td className="px-3 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                    {[event.city, event.state].filter(Boolean).join(', ') || '—'}
                  </td>
                  {/* Status - editable dropdown */}
                  <td className="px-3 py-3">
                    {canEdit ? (
                      <select
                        value={event.status}
                        onChange={(e) => handleStatusChange(event.id, e.target.value)}
                        className="rounded-full border-0 bg-transparent text-xs font-medium focus:outline-none focus:ring-1 focus:ring-[#503864] cursor-pointer"
                      >
                        <option value="active">active</option>
                        <option value="disabled">disabled</option>
                        <option value="past">past</option>
                      </select>
                    ) : (
                      <StatusBadge status={event.status} />
                    )}
                  </td>
                  {/* Actions */}
                  {(canEdit || canDelete) && (
                    <td className="px-3 py-3">
                      {canDelete && (
                        <>
                          {deleteConfirmId === event.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(event.id)}
                                className="text-xs text-red-600 hover:text-red-800 font-medium"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="text-xs text-gray-500 hover:text-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(event.id)}
                              className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                              title="Delete"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            variant="secondary"
          >
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <CustomButton
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            variant="secondary"
          >
            Next
          </CustomButton>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <EventCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  )
}
